import { useState } from "react";
import { Button } from "@/components/ui/button";
import { ArrowRight, CheckCircle2, X, ChevronLeft, ChevronRight } from "lucide-react";
import { useNavigate } from "react-router-dom";
import QuoteModal from "@/components/QuoteModal";
import NewsletterSignup from "@/components/NewsletterSignup";

const Home = () => {
  const navigate = useNavigate();
  const [showQuoteModal, setShowQuoteModal] = useState(false);
  const [lightboxImage, setLightboxImage] = useState(null);
  const [allImages, setAllImages] = useState([]);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  const openLightbox = (imageSrc, imageList, index) => {
    setAllImages(imageList);
    setCurrentImageIndex(index);
    setLightboxImage(imageSrc);
  };

  const closeLightbox = () => {
    setLightboxImage(null);
    setAllImages([]);
    setCurrentImageIndex(0);
  };

  const goToPrevious = (e) => {
    e.stopPropagation();
    const newIndex = currentImageIndex === 0 ? allImages.length - 1 : currentImageIndex - 1;
    setCurrentImageIndex(newIndex);
    setLightboxImage(allImages[newIndex]);
  };

  const goToNext = (e) => {
    e.stopPropagation();
    const newIndex = currentImageIndex === allImages.length - 1 ? 0 : currentImageIndex + 1;
    setCurrentImageIndex(newIndex);
    setLightboxImage(allImages[newIndex]);
  };

  const features = [
    "Custom designs tailored to your team",
    "High-quality, durable materials",
    "Perfect for sports clubs and organizations",
    "Affordable bulk pricing",
    "Exceptional customer care",
    "Professional branding options"
  ];

  const products = [
    {
      id: 1,
      name: "Basketball Pro Pack",
      image: "https://images.unsplash.com/photo-1758745369561-e963bc5202fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxhdGhsZXRpYyUyMGJhY2twYWNrc3xlbnwwfHx8fDE3NjI4Nzg3NDd8MA&ixlib=rb-4.1.0&q=85",
      description: "Designed for basketball teams with spacious compartments"
    },
    {
      id: 2,
      name: "Soccer Elite Pack",
      image: "https://images.unsplash.com/photo-1689395312653-56d4c2f14c2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhdGhsZXRpYyUyMGJhY2twYWNrc3xlbnwwfHx8fDE3NjI4Nzg3NDd8MA&ixlib=rb-4.1.0&q=85",
      description: "Perfect for soccer clubs with ventilated shoe compartment"
    },
    {
      id: 3,
      name: "Hockey Champion Pack",
      image: "https://images.pexels.com/photos/7010178/pexels-photo-7010178.jpeg",
      description: "Built tough for hockey teams with reinforced straps"
    },
    {
      id: 4,
      name: "All-Sport Versatile Pack",
      image: "https://images.unsplash.com/photo-1656428813604-6225efa90782?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzd8MHwxfHNlYXJjaHwxfHxzcG9ydCUyMGJhY2twYWNrc3xlbnwwfHx8fDE3NjI4Nzg3MzR8MA&ixlib=rb-4.1.0&q=85",
      description: "Adaptable design suitable for any sport or activity"
    }
  ];

  const testimonials = [
    {
      text: "Our custom backpacks are perfect! The quality, durability, and organized compartments make them ideal for our athletes. The personalized design adds a professional touch we love. Highly recommend Apa Custom!",
      author: "Pakmen Volleyball Club"
    },
    {
      text: "We have worked with several suppliers before, but the level of true customization here is unmatched. Apa didn't just slap a logo on a pre-made bag; every pocket, zipper, and fabric choice was tailored to our exact specs. The build quality is rugged enough to survive the day in and out of a young athlete, and the design team was incredibly patient with my endless tweaks. If you want a \"one-of-a-kind\" product that actually feels premium, Apa Custom is the place.",
      author: "Ottawa Fusion VC"
    },
    {
      text: "In our 10 years as a club we have never found a backpack that has this quality, size and customization at this value. Every athlete has said their backpack is the best piece of gear they have received.",
      author: "Galaxy Volleyball Club"
    },
    {
      text: "Our overall experience working with APA has been excellent. Their creativity, flexibility, and consultation during the design phase has given our academy's athletes and coaches backpacks that we absolutely love. We highly recommend APA.",
      author: "Bounce Volleyball Academy"
    },
    {
      text: "Working with APA Custom has been easy and seamless from start to finish. Their team is responsive, organized, and truly understands what clubs and athletes are looking for. The backpacks are excellent quality - durable, well-made, and fully customizable, which our athletes absolutely love. Even our ball bags came branded, which adds such a professional touch and really elevates our program's look. We're extremely happy with both the products and the experience, and would highly recommend APA Custom to any organization looking for high-quality, customizable gear with great service.",
      author: "Vaughan Legends Volleyball Club"
    },
    {
      text: "We wanted to thank Apa Custom for the awesome products for Bluewater Ballistix volleyball club. The backpacks for our coaches and staff were personalized and very well received as a holiday gift. The process and price point were very reasonable and you made it easy. Your patience and flexibility are appreciated.",
      author: "Bluewater Ballistix Volleyball Club"
    },
    {
      text: "We are pleased with the custom backpacks we ordered from APA. Attractive, good value and roomy with multiple compartments, perfect for the competitive volleyball player! Customer service has been responsive and supportive throughout every step of the process and the shipment arrived on time! Our teams stand out at competitions with these sharp looking backpacks! Thanks, Srdan!",
      author: "London Volleyball Club"
    },
    {
      text: "APA Custom absolutely delivered for our club. The backpacks are extremely durable, perfectly customized with our logo and colors, and built for real basketball life - practices, travel, and tournaments. Our players love them, and the quality speaks for itself. This was a smooth process from start to finish, and we couldn't be happier with the result.",
      author: "Vaughan Youth Athletic Association"
    },
    {
      text: "We recently ordered custom T-shirts, hoodies, and gym bags from Srdan at APA Custom, and we couldn't be happier with the results. The service was excellent from start to finish, great communication, creative designs, and high quality custom work. Pricing was fair and transparent, and everything was delivered exactly as promised. If you're looking for custom apparel or gear, reach out to Srdan and the team at APA Custom and they'll take great care of you.",
      author: "Leading Edge MMA Gym"
    },
    {
      text: "Our team is extremely impressed with the quality of this Volleyball bag and customization options. It looks great, highly functional and durable.",
      author: "Altitude Volleyball Club"
    },
    {
      text: "We couldn't be happier with our new backpacks! The quality is top tier, the design looks amazing, and they perfectly represent our brand. The whole process was smooth and professional. Our team loves them and we've had tons of compliments! Highly recommend.",
      author: "Toronto Connex Volleyball Club"
    },
    {
      text: "Northern Chill Volleyball had an excellent experience working with Srdan on our custom volleyball bags. From the beginning, the customer service was outstanding. Srdan was incredibly helpful with the artistic design process and worked with us to create a bag that truly reflected our club. The level of customization was exactly what we were looking for, and the final product exceeded our expectations. The bags are high quality, durable, and thoughtfully designed, with a large space for shoes and multiple compartments for everything a volleyball player needs. Srdan was also very transparent about the challenges of overseas shipping and kept us informed every step of the way, doing everything possible to ensure the bags arrived on time. We are extremely happy with the result and would highly recommend working with him.",
      author: "Northern Chill Volleyball Club"
    }
  ];

  const sports = [
    {
      name: "Basketball",
      image: "https://images.unsplash.com/photo-1758745369561-e963bc5202fe?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHw0fHxhdGhsZXRpYyUyMGJhY2twYWNrc3xlbnwwfHx8fDE3NjI4Nzg3NDd8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      name: "Soccer",
      image: "https://images.unsplash.com/photo-1689395312653-56d4c2f14c2b?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1Nzh8MHwxfHNlYXJjaHwxfHxhdGhsZXRpYyUyMGJhY2twYWNrc3xlbnwwfHx8fDE3NjI4Nzg3NDd8MA&ixlib=rb-4.1.0&q=85"
    },
    {
      name: "Hockey",
      image: "https://images.pexels.com/photos/7010178/pexels-photo-7010178.jpeg"
    }
  ];

  return (
    <div className="min-h-screen" data-testid="home-page">
      {/* Hero Section */}
      <section className="flex flex-col items-center pt-24 md:pt-28 bg-white" data-testid="hero-section">
        <div className="w-full flex items-center justify-center">
          <img
            src="https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/u5t426j8_IMG_3708.JPG"
            alt="Custom Backpacks"
            className="w-full max-w-4xl md:max-w-5xl lg:max-w-6xl h-auto object-contain px-4 md:px-6 -mb-44 md:-mb-80 lg:-mb-[30rem]"
          />
        </div>
        
        <div className="container mx-auto px-6 text-center pb-12 md:pb-16">
          <h1 className="text-4xl sm:text-5xl lg:text-7xl font-bold text-gray-900 mb-4 sm:mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Custom Sport<br />Backpacks
          </h1>
          <p className="text-base sm:text-lg lg:text-xl text-gray-800 mb-6 sm:mb-8 max-w-2xl mx-auto" style={{ fontFamily: 'Inter, sans-serif' }}>
            We develop high quality, affordable & durable sport backpacks customized for sport clubs & teams around the world
          </p>
          <Button
            size="lg"
            onClick={() => setShowQuoteModal(true)}
            className="bg-gray-900 hover:bg-gray-800 text-white px-6 sm:px-8 py-5 sm:py-6 text-base sm:text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
            data-testid="design-yours-btn"
          >
            Design Yours <ArrowRight className="ml-2" />
          </Button>
        </div>
      </section>

      {/* Company Introduction */}
      <section className="py-20 bg-white" data-testid="intro-section">
        <div className="container mx-auto px-6">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-6" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Are you part of a sports club?
            </h2>
            <p className="text-base sm:text-lg text-gray-700 mb-12" style={{ fontFamily: 'Inter, sans-serif' }}>
              Apa Custom is a company built from the recognition that all sports should have access to custom backpacks that suit their specific needs. We specialize in assisting sport clubs, sport leagues and all sport related organizations to optimize their brand awareness through our quality, affordable and durable sport backpacks.
            </p>
            <Button
              onClick={() => navigate('/about')}
              variant="outline"
              size="lg"
              className="border-2 border-gray-900 text-gray-900 hover:bg-gray-100 px-8 py-6 text-lg rounded-full transition-all duration-300"
              data-testid="learn-more-btn"
            >
              Learn More
            </Button>
          </div>
        </div>
      </section>

      {/* Product Showcase */}
      <section className="py-20 bg-white" data-testid="products-section">
        <div className="container mx-auto px-6">
          <div className="text-center mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              Custom Designs
            </h2>
            <p className="text-base sm:text-lg text-gray-700" style={{ fontFamily: 'Inter, sans-serif' }}>
              See our quality craftsmanship and customization in action
            </p>
          </div>

          {/* Product Group 1 - Pakmen Volleyball */}
          {(() => {
            const group1Images = [
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/uurcp59f_Pakmen_BackPack_2024_Front_6.5x6.5_RGB_HR.JPEG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/n01py2ip_Pakmen_BackPack_2024_6.5x6.5_RGB_HR.JPEG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/7vhnuzyi_Pakmen_Backpack_2024_LeftSide_6.5x6.5_RGB_HR.JPEG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/95nh9if1_Pakmen_BackPack_2024_OpenWithBall_6.5x6.5_RGB_HR.JPEG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/jbn3whqa_Pakmen_Backpack_2024_RightSide_6.5x6.5_RGB_HR.JPEG"
            ];
            return (
              <div className="mb-20 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-5 gap-4">
                  {group1Images.map((src, index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl aspect-square bg-white cursor-pointer"
                      onClick={() => openLightbox(src, group1Images, index)}
                    >
                      <img
                        src={src}
                        alt={`Pakmen Backpack ${index + 1}`}
                        className="w-full h-full object-contain hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Product Group 2 - Ottawa Fusion */}
          {(() => {
            const group2Images = [
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/yq8ewhx4_IMG_3305.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/9ernw0gq_IMG_3306.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/ctmvxbur_IMG_3309.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/umbqe95m_IMG_3310.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/9h2a5lvg_IMG_3315.JPG"
            ];
            return (
              <div className="mb-20 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-5 gap-4">
                  {group2Images.map((src, index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl aspect-square bg-white cursor-pointer"
                      onClick={() => openLightbox(src, group2Images, index)}
                    >
                      <img
                        src={src}
                        alt={`Ottawa Fusion Backpack ${index + 1}`}
                        className="w-full h-full object-contain hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Product Group 3 - Lyndon Volleyball */}
          {(() => {
            const group3Images = [
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/rpbns3sc_IMG_3156.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/d5e0c2q4_IMG_3158.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/six0k61x_IMG_3159.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/14v66s3y_IMG_3165.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/6c4c86rk_IMG_3168.JPG"
            ];
            return (
              <div className="mb-20 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-5 gap-4">
                  {group3Images.map((src, index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl aspect-square bg-white cursor-pointer"
                      onClick={() => openLightbox(src, group3Images, index)}
                    >
                      <img
                        src={src}
                        alt={`Lyndon Volleyball Backpack ${index + 1}`}
                        className="w-full h-full object-contain hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Product Group 5 - CONNEX */}
          {(() => {
            const group5Images = [
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/05b11px4_IMG_2416.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/s3h05yuu_IMG_2417.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/gumcvorj_IMG_2420.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/b40f5l86_IMG_2422.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/mww51s7e_IMG_2425.JPG"
            ];
            return (
              <div className="mb-20 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-5 gap-4">
                  {group5Images.map((src, index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl aspect-square bg-white cursor-pointer"
                      onClick={() => openLightbox(src, group5Images, index)}
                    >
                      <img
                        src={src}
                        alt={`CONNEX Backpack ${index + 1}`}
                        className="w-full h-full object-contain hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Product Group 6 - Northern Chill */}
          {(() => {
            const group6Images = [
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/r4altnlj_IMG_3143.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/ij9rjeyz_IMG_3144.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/hx2hplyb_IMG_3146.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/mu03fg8p_IMG_3147.JPG",
              "https://customer-assets.emergentagent.com/job_yourpackstory/artifacts/xzo23ysd_IMG_3153.JPG"
            ];
            return (
              <div className="mb-20 max-w-7xl mx-auto">
                <div className="grid md:grid-cols-5 gap-4">
                  {group6Images.map((src, index) => (
                    <div 
                      key={index}
                      className="relative overflow-hidden rounded-2xl aspect-square bg-white cursor-pointer"
                      onClick={() => openLightbox(src, group6Images, index)}
                    >
                      <img
                        src={src}
                        alt={`Northern Chill Backpack ${index + 1}`}
                        className="w-full h-full object-contain hover:scale-110 transition-transform duration-500"
                      />
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          <div className="text-center mt-16">
            <Button
              onClick={() => setShowQuoteModal(true)}
              size="lg"
              className="bg-gray-900 hover:bg-gray-800 text-white px-8 py-6 text-lg rounded-full transition-all duration-300 hover:scale-105 shadow-lg"
              data-testid="get-quote-btn"
            >
              Get Your Custom Design
            </Button>
          </div>
        </div>
      </section>

      {/* Customization Highlights */}
      <section className="py-20 bg-gray-50" data-testid="features-section">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            Why Choose Apa Custom?
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {features.map((feature, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300 border border-gray-100"
                data-testid={`feature-${index}`}
              >
                <CheckCircle2 className="w-8 h-8 text-gray-900 mb-4" />
                <p className="text-gray-800 text-base" style={{ fontFamily: 'Inter, sans-serif' }}>
                  {feature}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials */}
      <section className="py-20 bg-gray-50" data-testid="testimonials-section">
        <div className="container mx-auto px-6">
          <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-12 text-center" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
            What Our Clients Say
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8 max-w-7xl mx-auto">
            {testimonials.map((testimonial, index) => (
              <div
                key={index}
                className="bg-white p-8 rounded-2xl shadow-sm hover:shadow-md transition-all duration-300"
                data-testid={`testimonial-${index}`}
              >
                <p className="text-gray-700 mb-6 text-base leading-relaxed" style={{ fontFamily: 'Inter, sans-serif' }}>
                  &ldquo;{testimonial.text}&rdquo;
                </p>
                <p className="font-bold text-gray-900" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
                  {testimonial.author}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Quote Modal */}
      <QuoteModal open={showQuoteModal} onClose={() => setShowQuoteModal(false)} />

      {/* Image Lightbox */}
      {lightboxImage && (
        <div 
          className="fixed inset-0 z-50 bg-black/90 flex items-center justify-center"
          onClick={closeLightbox}
        >
          {/* Close button */}
          <button
            onClick={closeLightbox}
            className="absolute top-4 right-4 text-white hover:text-gray-300 transition-colors z-50"
          >
            <X className="w-8 h-8" />
          </button>

          {/* Previous button */}
          {allImages.length > 1 && (
            <button
              onClick={goToPrevious}
              className="absolute left-4 md:left-8 text-white hover:text-gray-300 transition-colors z-50 bg-black/50 rounded-full p-2"
            >
              <ChevronLeft className="w-8 h-8" />
            </button>
          )}

          {/* Image */}
          <div 
            className="max-w-[90vw] max-h-[90vh] flex items-center justify-center"
            onClick={(e) => e.stopPropagation()}
          >
            <img
              src={lightboxImage}
              alt="Enlarged view"
              className="max-w-full max-h-[90vh] object-contain"
            />
          </div>

          {/* Next button */}
          {allImages.length > 1 && (
            <button
              onClick={goToNext}
              className="absolute right-4 md:right-8 text-white hover:text-gray-300 transition-colors z-50 bg-black/50 rounded-full p-2"
            >
              <ChevronRight className="w-8 h-8" />
            </button>
          )}

          {/* Image counter */}
          {allImages.length > 1 && (
            <div className="absolute bottom-4 left-1/2 -translate-x-1/2 text-white text-sm bg-black/50 px-3 py-1 rounded-full">
              {currentImageIndex + 1} / {allImages.length}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Home;